import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button } from '@ideoz/ui';
import { fixFilenameMojibake } from '@ideoz/core';
import { UploadFilesDialog } from './components/UploadFilesDialog';
import { GOOGLE_AUTH_CONFIG } from '../../config/googleAuth';
import type { FilesOwner } from './files.types';
import { useFiles } from './useFiles';
import { FilesHeader } from './components/FilesHeader';
import { FilesList } from './components/FilesList';
import { FilesEmptyState } from './components/FilesEmptyState';

/** Owner-specific copy — the only thing that differs between contexts. */
const COPY: Record<FilesOwner['type'], { description: string; emptyBody: string }> = {
  startup: {
    description: 'Specs, mockups, and reference material attached to this startup.',
    emptyBody:
      'No files attached yet. Upload specs or references so Ideoz can ground its work in your real context.',
  },
  scope: {
    description: 'Specs, mockups, and reference material attached to this scope.',
    emptyBody:
      'No files attached yet. Upload specs or mockups so Ideoz can ground the scope work in your real context.',
  },
};

interface FilesModuleProps {
  owner: FilesOwner;
}

/**
 * Files feature module. Owns no owner-specific branching beyond the `owner`
 * prop it forwards to `useFiles` and the copy lookup — the same component
 * renders for a startup and for a scope.
 */
export function FilesModule({ owner }: FilesModuleProps): JSX.Element {
  const { attachments, isLoading, deletingId, fetchAttachments, uploadFiles, deleteFile } =
    useFiles(owner);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  // Delete goes through a confirmation: the trash button opens this, the actual
  // delete only runs on confirm. Holds the attachment id, or null when closed.
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const copy = COPY[owner.type];

  const pendingAttachment = fileToDelete
    ? attachments.find((a) => a.id === fileToDelete)
    : undefined;
  const pendingName = pendingAttachment
    ? fixFilenameMojibake(
        pendingAttachment.fileName ??
          pendingAttachment.name ??
          pendingAttachment.originalName ??
          'this file'
      )
    : 'this file';

  return (
    <section className="mx-auto w-full max-w-5xl px-6 py-10">
      <FilesHeader description={copy.description} onUpload={() => setIsUploadOpen(true)} />

      <div className="bg-surface rounded-2xl border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-gray-300 dark:text-gray-500 animate-spin" />
          </div>
        ) : attachments.length === 0 ? (
          <FilesEmptyState body={copy.emptyBody} onUpload={() => setIsUploadOpen(true)} />
        ) : (
          <FilesList attachments={attachments} deletingId={deletingId} onDelete={setFileToDelete} />
        )}
      </div>

      <UploadFilesDialog
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        projectAttachments={attachments as any}
        onFilesUpload={uploadFiles}
        onFileDelete={deleteFile}
        onFetchAttachments={fetchAttachments}
        isWeb
        googleAuthConfig={GOOGLE_AUTH_CONFIG}
        projectId={owner.startupId || undefined}
      />

      {/* Delete confirmation */}
      <Dialog
        open={!!fileToDelete}
        onOpenChange={(open) => {
          if (!open) setFileToDelete(null);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete file?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium break-all">{pendingName}</span> will be permanently
            removed. This can’t be undone.
          </p>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setFileToDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!!deletingId}
              onClick={() => {
                const id = fileToDelete;
                setFileToDelete(null);
                if (id) void deleteFile(id);
              }}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
